import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import Fuse from 'fuse.js';
import * as mapbox from 'mapbox-gl';
import { BehaviorSubject, combineLatest, Observable, Subject, timer } from 'rxjs';
import { debounceTime, filter, map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { IntercomService } from '../../root/intercom.service';
import { fuse } from '../../utilities/fuse';
import { HoneyMapDriver, HoneyMapService } from '../honey-map.service';

const searchOptions: Fuse.IFuseOptions<HoneyMapDriver> = {
  distance: 100,
  keys: ['phoneNumber', 'name'],
  location: 0,
  minMatchCharLength: 1,
  shouldSort: true,
  threshold: 0.2,
};

const STREETS = 'mapbox://styles/mapbox/streets-v11';
const SATELLITE = 'mapbox://styles/mapbox/satellite-v9';

@Component({
  selector: 'lohi-honey-map',
  templateUrl: './honey-map.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HoneyMapComponent implements OnInit, AfterViewInit, OnDestroy {
  private driverMarkers: Record<string, mapbox.Marker> = {};
  private me: mapbox.Marker;
  private map: mapbox.Map;
  private streets = true;
  private drivers$$ = new BehaviorSubject<HoneyMapDriver[]>([]);
  public onlyFiveFControl = new FormControl('All');
  public onlyHALControl = new FormControl('All');
  public onlyBoxersControl = new FormControl('All');
  public lastUpdatedWithinControl = new FormControl(1);
  public searchBoxControl = new FormControl(null);

  @ViewChildren('map') private mapElements: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChild('filtersModal') private filtersModal: TemplateRef<any>;
  private destroy$$ = new Subject();
  public searchableDriverList$: Observable<HoneyMapDriver[]>;

  constructor(
    private intercom: IntercomService,
    private honeyMapService: HoneyMapService,
    private matDialog: MatDialog,
  ) {
    (mapbox as any).accessToken =
      'pk.eyJ1Ijoic2F0ZXN0ZGV2IiwiYSI6ImNrYmVraDllZDBtcXQycHFlbzVqYXk0cnMifQ.HzVySDvy0mLDW03ykD2HNw';
  }

  public ngOnInit(): void {
    this.intercom.shutdown();
    this.searchableDriverList$ = combineLatest([
      this.drivers$$.pipe(map(fuse(searchOptions))),
      this.searchBoxControl.valueChanges.pipe(startWith('')),
    ]).pipe(
      map(([fused, searchTerm]) => {
        if (!fused) {
          return [];
        }
        if (!searchTerm) {
          return fused.data.sort((a, b) => a.name.localeCompare(b.name));
        }
        return fused.fuse
          .search(searchTerm)
          .map((d) => d.item)
          .sort((a, b) => a.name.localeCompare(b.name));
      }),
    );
  }

  public ngAfterViewInit(): void {
    this.initMap();
  }

  private async initMap() {
    // Make sure we have a map element
    if (this.mapElements.length === 0) {
      await this.mapElements.changes
        .pipe(
          filter((changes: QueryList<any>) => {
            return changes.length > 0;
          }),
          take(1),
        )
        .toPromise();
    }

    const options: mapbox.MapboxOptions = {
      container: this.mapElements.first.nativeElement,
      style: 'mapbox://styles/mapbox/streets-v11',
    };

    this.map = new mapbox.Map(options);
    this.map.setZoom(12);
    this.map.setCenter({ lat: 39.8283, lng: -98.5795 });
    this.map.addControl(new mapbox.NavigationControl());
    this.map.addControl(new mapbox.FullscreenControl());
    this.map.once('style.load', () => {
      this.listenToMapInfo();
      this.navToCurrent();
    });

    this.map.on('moveend', (ev) => {
      this.drawDrivers(this.drivers$$.value);
    });

    this.map.on('dragend', (ev) => {
      this.drawDrivers(this.drivers$$.value);
    });
  }

  private listenToMapInfo() {
    combineLatest([
      this.onlyFiveFControl.valueChanges.pipe(startWith(null)),
      this.onlyHALControl.valueChanges.pipe(startWith(null)),
      this.onlyBoxersControl.valueChanges.pipe(startWith(null)),
      this.lastUpdatedWithinControl.valueChanges.pipe(startWith(1)),
      timer(1000, 10000),
    ])
      .pipe(
        switchMap(([onlyFiveF, onlyHAL, onlyBoxers, lastUpdatedWithin]) =>
          this.honeyMapService.getMapData$(onlyFiveF, onlyHAL, onlyBoxers, lastUpdatedWithin),
        ),
        takeUntil(this.destroy$$),
        debounceTime(200),
      )
      .subscribe((drivers) => {
        if (!drivers) {
          Object.values(this.driverMarkers).forEach((marker) => marker.remove());
          this.driverMarkers = {};
          return;
        }

        this.drawDrivers(drivers);

        /*if (hasChanges) {
          const bounds = new mapbox.LngLatBounds();

          [...Object.values(this.driverMarkers)].forEach((marker) => {
            bounds.extend(marker.getLngLat());
          });

          if (bounds.isEmpty()) {
            return;
          }
        }*/
        this.drivers$$.next(drivers);
        this.updateMe(null);
      });
  }

  public updateMe(position: Position | null) {
    if (position) {
      if (!this.me) {
        const me = createMeMarker(position);
        me.addTo(this.map);
        this.me = me;
      } else {
        this.me.setLngLat({ lng: position.coords.longitude, lat: position.coords.latitude });
      }
    } else {
      navigator.geolocation.getCurrentPosition((position2) => {
        this.updateMe(position2);
      });
    }
  }

  public navToCurrent() {
    navigator.geolocation.getCurrentPosition((position) => {
      this.updateMe(position);
      this.map.panTo({ lat: position.coords.latitude, lng: position.coords.longitude }, { animate: true });
    });
  }

  private drawDrivers(drivers: HoneyMapDriver[]): boolean {
    let hasChanges = false;
    const markersIdsToRemove = [];
    const bounds = this.map.getBounds();
    for (const driver of Object.entries(this.driverMarkers)) {
      let found = false;
      for (const driver1 of drivers) {
        if (driver[0] === driver1.phone && bounds.contains(driver[1].getLngLat())) {
          found = true;
          break;
        }
      }
      if (!found) {
        driver[1].remove();
        markersIdsToRemove.push(driver[0]);
        hasChanges = true;
      }
    }

    markersIdsToRemove.forEach((id) => {
      delete this.driverMarkers[id];
    });

    for (const driver of drivers) {
      if (this.driverMarkers[driver.phone] && bounds.contains({ lng: driver.longitude, lat: driver.latitude })) {
        this.driverMarkers[driver.phone].setLngLat({ lng: driver.longitude, lat: driver.latitude });
      } else {
        if (!bounds.contains({ lng: driver.longitude, lat: driver.latitude })) {
          continue;
        }
        const newMarker = createDriverMarker(driver);
        this.driverMarkers[driver.phone] = newMarker;
        newMarker.addTo(this.map);
        hasChanges = true;
      }
    }
    return hasChanges;
  }

  public toggleStyle() {
    if (this.streets) {
      this.map.setStyle(SATELLITE);
      this.streets = false;
    } else {
      this.map.setStyle(STREETS);
      this.streets = true;
    }
  }

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.unsubscribe();
  }

  public navToDriver(driver: HoneyMapDriver) {
    this.map.panTo({ lat: driver.latitude, lng: driver.longitude }, { animate: true });
  }

  public promptFilters() {
    this.matDialog.open(this.filtersModal);
  }
}

function createDriverMarker(mappable: HoneyMapDriver): mapbox.Marker {
  const el = document.createElement('div');
  el.className = 'w-3 h-3 rounded-full text-center text-white border border-white';
  el.style.backgroundColor = '#08A9E9';
  el.style.lineHeight = '24pzx';
  if (mappable.isFiveF) {
    el.innerText = '5F';
    el.style.backgroundColor = '#FEB300';
  }
  if (localStorage.getItem('beeMode')) {
    el.innerText = '🐝';
    el.style.backgroundColor = '#FEB300';
  }
  if (localStorage.getItem('showNamesOnHoneyMap')) {
    const label = document.createElement('div');
    label.className = addToBaseLabel('bg-gray-700');
    if (mappable.isFiveF) {
      label.innerText = mappable.name;
    }
    label.style.top = '24px';
    label.style.left = '12px';
    label.style.lineHeight = '23px';
    label.style.transform = 'translate(-50%, 0)';
    label.style.fontSize = '14px';
    el.appendChild(label);
  }

  const marker = new mapbox.Marker(el).setLngLat({ lng: mappable.longitude, lat: mappable.latitude });
  const text = mappable.isFiveF
    ? `<div class="text-2xl">
<div class="font-bold">${mappable.name}</div>
<span>Last Updated:<br> ${new Date(mappable.lastUpdated).toLocaleString()}</span>
</div>`
    : `<div class="text-2xl">
<span>Last Updated:<br> ${new Date(mappable.lastUpdated).toLocaleString()}</span>
</div>`;
  const popup = new mapbox.Popup().setHTML(text);
  marker.setPopup(popup);
  return marker;
}

function createMeMarker(mappable: Position): mapbox.Marker {
  const el = document.createElement('div');
  el.className = 'w-3 h-3 rounded-full text-center text-white border border-white';
  el.style.backgroundColor = '#0e4eaf';
  el.style.lineHeight = '24px';
  const label = document.createElement('div');
  label.className = addToBaseLabel('bg-blue-600');
  label.innerText = 'Me';
  label.style.top = '24px';
  label.style.left = '12px';
  label.style.lineHeight = '23px';
  label.style.transform = 'translate(-50%, 0)';
  label.style.fontSize = '14px';
  el.appendChild(label);

  const marker = new mapbox.Marker(el).setLngLat({ lng: mappable.coords.longitude, lat: mappable.coords.latitude });
  return marker;
}

function addToBaseLabel(classes: string): string {
  return `text-white absolute whitespace-no-wrap pl-1 pr-1 rounded-md ${classes}`;
}
